import { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';
import { Bell } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { useWebSocket } from '../hooks/useWebSocket';
import { VirtualizedList } from './VirtualizedList';
import logger from '../utils/logger';

const NotificationCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  
  // Use WebSocket for real-time unread count (replaces polling)
  const { unreadCount } = useWebSocket();

  useEffect(() => {
    if (open) {
      loadNotifications();
    }
  }, [open]);

  const loadNotifications = async () => {
    try {
      const { data } = await api.get(`/notifications`, {
        withCredentials: true,
      });
      setNotifications(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des notifications');
    }
  };

      const handleNotificationClick = async (notif) => {
    // Mark as read
    try {
      await api.post(`/notifications/${notif.id}/read`, {}, { withCredentials: true });
      
      // Navigate based on type
      if (notif.type === 'group_class_invitation' || 
          notif.type === 'group_class_registered' || 
          notif.type === 'group_class_confirmed') {
        navigate('/group-classes');
      } else if (notif.relatedMatchId) {
        navigate(`/matches/${notif.relatedMatchId}`);
      }
      
      setOpen(false);
      loadUnreadCount();
    } catch (error) {
      logger.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.post(`/notifications/mark-all-read`, {}, { withCredentials: true });
      toast.success('Toutes les notifications ont été marquées comme lues');
      loadNotifications();
      loadUnreadCount();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      match_joined: '👥',
      match_full: '✅',
      match_confirmed: '✅',
      match_invitation: '✉️',
      player_left: '🚪',
      match_result: '🏆',
      new_message: '💬',
      friend_request: '🤝',
      friend_accepted: '❤️',
      booking_confirmed: '🎾',
      group_class_invitation: '🎓',
      group_class_registered: '📝',
      group_class_confirmed: '✅',
    };
    return icons[type] || '🔔';
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-testid="notification-bell"
          className="relative theme-text hover:theme-accent"
        >
          <Bell size={24} className={unreadCount > 0 ? 'fill-current' : ''} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold theme-text">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[400px] theme-bg border-l theme-border">
        <SheetHeader>
          <SheetTitle className="theme-text font-black text-xl">NOTIFICATIONS</SheetTitle>
        </SheetHeader>

        {notifications.length > 0 && unreadCount > 0 && (
          <Button
            onClick={handleMarkAllRead}
            variant="ghost"
            size="sm"
            className="w-full mt-2 theme-accent hover:theme-text"
          >
            Tout marquer comme lu
          </Button>
        )}

        <div className="mt-6 max-h-[calc(100vh-180px)]">
          {notifications.length === 0 ? (
            <div className="text-center py-12 theme-text-secondary">
              Aucune notification
            </div>
          ) : (
            <VirtualizedList
              items={notifications}
              estimateSize={100}
              className="h-[calc(100vh-200px)]"
              renderItem={(notif) => (
                <div className="pb-2">
                  <div
                    onClick={() => handleNotificationClick(notif)}
                    data-testid={`notification-${notif.id}`}
                    className={`p-4 rounded-xl cursor-pointer transition-colors ${
                      notif.read
                        ? 'theme-bg-card hover:theme-bg-card'
                        : 'bg-[#00d4ff]/10 border border-[#00d4ff]/30 hover:bg-[#00d4ff]/20'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{getNotificationIcon(notif.type)}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-bold theme-text text-sm">{notif.title}</p>
                          {!notif.read && (
                            <span className="w-2 h-2 rounded-full bg-[#00d4ff] flex-shrink-0 mt-1"></span>
                          )}
                        </div>
                        <p className="text-sm theme-text-secondary mt-1 line-clamp-2">
                          {notif.message}
                        </p>
                        <p className="text-xs text-zinc-600 mt-2">
                          {format(new Date(notif.createdAt), "d MMM 'à' HH:mm", {
                            locale: fr,
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default NotificationCenter;
